import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, KeyRound, Mail, User as UserIcon } from 'lucide-react';
import { authApi } from './api';
import { OAuthButton } from './OAuthButton';
import { FlowBackground } from './FlowBackground';
import { WaveBackdrop } from './WaveBackdrop';
import { AuthFooter } from './AuthFooter';
import {
  registerAccountStepSchema,
  registerCompanyStepSchema,
  type RegisterAccountInput,
  type RegisterCompanyInput,
} from '@/lib/schemas/auth';
import { useAuthStore } from '@/lib/auth/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { usePlanContext } from '@onxite/plan-sdk';

type Step = 1 | 2 | 3;

interface PendingCompany {
  companyName: string;
}
interface PendingAccount extends PendingCompany {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const t = useT();
  const { reportPlanAlert } = usePlanContext();

  const [step, setStep] = useState<Step>(1);
  const [company, setCompany] = useState<PendingCompany | null>(null);
  const [account, setAccount] = useState<PendingAccount | null>(null);
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const companyForm = useForm<RegisterCompanyInput>({
    resolver: zodResolver(registerCompanyStepSchema),
    defaultValues: { companyName: company?.companyName ?? '' },
  });
  const accountForm = useForm<RegisterAccountInput>({
    resolver: zodResolver(registerAccountStepSchema),
    defaultValues: {
      firstname: account?.firstname ?? '',
      lastname: account?.lastname ?? '',
      email: account?.email ?? '',
      password: '',
      confirmPassword: '',
    },
  });

  const initiate = useMutation({
    mutationFn: (input: PendingAccount) =>
      authApi.register.initiate({
        companyName: input.companyName,
        firstname: input.firstname,
        lastname: input.lastname,
        email: input.email,
        password: input.password,
      }),
    onSuccess: (data, input) => {
      setAccount(input);
      setStep(3);
      setResendCooldown(30);
      setOtp('');
      if (data.devHint) {
        toast({
          tone: 'info',
          title: t('register.toast.devHint'),
          description: data.devHint,
        });
      }
    },
    onError: (err: any) => {
      const message: string = err?.response?.data?.message ?? '';
      if (
        err?.response?.status === 409 ||
        message.includes('email-already-registered')
      ) {
        toast({
          tone: 'error',
          title: t('register.toast.emailTaken'),
          description: t('register.toast.emailTakenBody'),
        });
        return;
      }
      toast({
        tone: 'error',
        title: t('register.toast.initiateFailed'),
      });
    },
  });

  const verify = useMutation({
    mutationFn: (code: string) =>
      authApi.register.verify({ email: account!.email, otp: code }),
    onSuccess: (data) => {
      setSession({
        token: data.access_token,
        refreshToken: data.refresh_token ?? null,
        user: data.user,
        role: data.role,
      });
      reportPlanAlert((data as any).planAlert ?? null);
      toast({
        tone: 'success',
        title: t('register.toast.welcome', { name: account?.firstname ?? '' }),
        description: t('register.toast.welcomeBody'),
      });
      navigate('/');
    },
    onError: () => {
      toast({ tone: 'error', title: t('register.toast.invalidOtp') });
      setOtp('');
    },
  });

  // Auto-submit OTP when the 6 digits are typed (mirrors OtpPage behavior).
  useEffect(() => {
    if (step === 3 && otp.length === 6 && !verify.isPending) {
      verify.mutate(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  useEffect(() => {
    if (step === 3) otpInputRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleCompanySubmit = (values: RegisterCompanyInput) => {
    setCompany(values);
    setStep(2);
  };

  const handleAccountSubmit = (values: RegisterAccountInput) => {
    if (!company) {
      setStep(1);
      return;
    }
    initiate.mutate({
      companyName: company.companyName,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      password: values.password,
    });
  };

  const handleResend = () => {
    if (!account || resendCooldown > 0 || initiate.isPending) return;
    // Re-send by re-initiating with the same data — backend clears the prior
    // pending stash and issues a fresh OTP. UX-wise this is equivalent to a
    // resend button but keeps the password+name authoritative on the server.
    initiate.mutate(account);
  };

  return (
    <div className="relative grid min-h-screen lg:grid-cols-2">
      {/* Same backdrop as LoginPage: dot-grid + corner glows + vignette. */}
      <FlowBackground />
      {/* Left — form inside an elevated glass card so it floats above the grid. */}
      <div className="relative z-10 flex items-center justify-center px-3 py-8 sm:px-6 sm:py-12">
        <AuthFooter />
        <div
          className="w-full max-w-md rounded-2xl border border-white/[0.06] bg-[rgb(10_6_19/0.55)] p-5 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.7)] backdrop-blur-xl sm:p-8"
          style={{
            boxShadow:
              '0 30px 80px -20px rgb(0 0 0 / 0.7), 0 0 0 1px color-mix(in srgb, var(--color-xk-accent) 8%, transparent) inset',
          }}
        >
          <div className="mb-8 flex items-center gap-2">
            <img src="/xk-icon.svg" alt="Xamplin" className="h-9 w-9 rounded-lg" />
            <span className="font-display text-lg font-bold tracking-tight">xamplin</span>
          </div>

          <Stepper current={step} t={t} />

          {step === 1 && (
            <StepCompany
              t={t}
              form={companyForm}
              onSubmit={handleCompanySubmit}
            />
          )}

          {step === 2 && (
            <StepAccount
              t={t}
              form={accountForm}
              onSubmit={handleAccountSubmit}
              loading={initiate.isPending}
              onBack={() => setStep(1)}
              companyName={company?.companyName ?? ''}
            />
          )}

          {step === 3 && account && (
            <StepVerify
              t={t}
              email={account.email}
              otp={otp}
              onOtpChange={setOtp}
              onVerify={() => verify.mutate(otp)}
              verifying={verify.isPending}
              onResend={handleResend}
              resendCooldown={resendCooldown}
              resending={initiate.isPending}
              onBack={() => setStep(2)}
              inputRef={otpInputRef}
            />
          )}

          <p className="mt-8 text-center text-sm text-[var(--color-fg-muted)]">
            {t('register.haveAccount')}{' '}
            <Link to="/login" className="font-semibold text-[var(--color-accent)] hover:underline">
              {t('register.signIn')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right — hero with the same composition as LoginPage: transparent
          gradient base + low-opacity XK wave watermark + centred accent glow. */}
      <div
        className="relative z-10 hidden overflow-hidden lg:flex lg:items-center lg:justify-center lg:p-12"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--color-xk-navy) 92%, transparent) 0%, color-mix(in srgb, var(--color-xk-navy-mid) 95%, transparent) 60%, color-mix(in srgb, var(--color-xk-bg-deep, var(--color-xk-navy-mid)) 92%, transparent) 100%)',
        }}
      >
        {/* XK monogram wave silhouettes as a subtle brand watermark. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 text-[var(--color-xk-accent)] opacity-[0.08]"
          style={{
            maskImage:
              'radial-gradient(ellipse 75% 70% at 50% 50%, black 30%, transparent 90%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 70% at 50% 50%, black 30%, transparent 90%)',
          }}
        >
          <WaveBackdrop />
        </div>
        {/* Inner accent glow centred behind the logo / hero text. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 55% at 50% 45%, color-mix(in srgb, var(--color-xk-accent) 18%, transparent) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-md text-center">
          <img src="/xk-icon.svg" alt="Xamplin" className="mb-6 inline-block h-20 w-20 rounded-2xl" />
          <h2 className="text-3xl font-bold">{t('register.hero.title')}</h2>
          <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
            {t('register.hero.body')}
          </p>
        </div>
      </div>
    </div>
  );
}

function Stepper({ current, t }: { current: Step; t: ReturnType<typeof useT> }) {
  const labels = [
    t('register.step.company'),
    t('register.step.account'),
    t('register.step.verify'),
  ];
  return (
    <div className="mb-8 flex items-center gap-3">
      {labels.map((label, idx) => {
        const n = (idx + 1) as Step;
        const active = current === n;
        const done = current > n;
        return (
          <div key={label} className="flex flex-1 items-center gap-2">
            <span
              className={
                'grid h-7 w-7 place-items-center rounded-full text-xs font-bold transition-colors ' +
                (done
                  ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                  : active
                  ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)] ring-2 ring-[var(--color-accent)]'
                  : 'bg-[var(--color-bg-surface)] text-[var(--color-fg-muted)]')
              }
            >
              {n}
            </span>
            <span
              className={
                'text-xs font-semibold ' +
                (active ? 'text-[var(--color-fg-primary)]' : 'text-[var(--color-fg-muted)]')
              }
            >
              {label}
            </span>
            {idx < labels.length - 1 && (
              <span className="flex-1 border-t border-dashed border-[var(--color-border-default)]" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepCompany({
  t,
  form,
  onSubmit,
}: {
  t: ReturnType<typeof useT>;
  form: ReturnType<typeof useForm<RegisterCompanyInput>>;
  onSubmit: (v: RegisterCompanyInput) => void;
}) {
  return (
    <>
      <h1 className="text-3xl font-bold">{t('register.company.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
        {t('register.company.subtitle')}
      </p>

      <div className="mt-6">
        <div className="relative flex items-center">
          <div className="flex-grow border-t border-[var(--color-border-default)]" />
          <span className="mx-3 text-xs uppercase tracking-wider text-[var(--color-fg-subtle)]">
            {t('register.orWithSocial')}
          </span>
          <div className="flex-grow border-t border-[var(--color-border-default)]" />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <OAuthButton provider="google" label="Google" />
          <OAuthButton provider="linkedin" label="LinkedIn" />
          <OAuthButton provider="facebook" label="Facebook" comingSoon />
          <OAuthButton provider="apple" label="Apple" comingSoon />
        </div>
        <p className="mt-3 text-center text-xs text-[var(--color-fg-subtle)]">
          {t('register.orWithSocialHint')}
        </p>
      </div>

      <div className="my-6 relative flex items-center">
        <div className="flex-grow border-t border-[var(--color-border-default)]" />
        <span className="mx-3 text-xs uppercase tracking-wider text-[var(--color-fg-subtle)]">
          {t('register.orWithEmail')}
        </span>
        <div className="flex-grow border-t border-[var(--color-border-default)]" />
      </div>

      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          label={t('register.company.companyName')}
          error={form.formState.errors.companyName?.message}
          required
        >
          <div className="relative">
            <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <Input
              type="text"
              autoFocus
              placeholder={t('register.company.companyNamePlaceholder')}
              className="pl-9"
              {...form.register('companyName')}
            />
          </div>
        </FormField>
        <Button type="submit" className="w-full" size="lg">
          {t('register.company.continue')}
        </Button>
      </form>
    </>
  );
}

function StepAccount({
  t,
  form,
  onSubmit,
  loading,
  onBack,
  companyName,
}: {
  t: ReturnType<typeof useT>;
  form: ReturnType<typeof useForm<RegisterAccountInput>>;
  onSubmit: (v: RegisterAccountInput) => void;
  loading: boolean;
  onBack: () => void;
  companyName: string;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>
      <h1 className="text-3xl font-bold">{t('register.account.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
        {t('register.account.subtitlePrefix')}{' '}
        <span className="font-semibold text-[var(--color-accent)]">{companyName}</span>
        {'.'}
      </p>

      <form className="mt-6 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={t('register.account.firstname')}
            error={form.formState.errors.firstname?.message}
            required
          >
            <Input type="text" autoFocus {...form.register('firstname')} />
          </FormField>
          <FormField
            label={t('register.account.lastname')}
            error={form.formState.errors.lastname?.message}
            required
          >
            <Input type="text" {...form.register('lastname')} />
          </FormField>
        </div>
        <FormField
          label={t('register.account.email')}
          error={form.formState.errors.email?.message}
          required
        >
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@brand.com"
              className="pl-9"
              {...form.register('email')}
            />
          </div>
        </FormField>
        <FormField
          label={t('register.account.password')}
          error={form.formState.errors.password?.message}
          required
          hint={t('register.account.passwordHint')}
        >
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <Input
              type="password"
              autoComplete="new-password"
              className="pl-9"
              {...form.register('password')}
            />
          </div>
        </FormField>
        <FormField
          label={t('register.account.confirmPassword')}
          error={form.formState.errors.confirmPassword?.message}
          required
        >
          <Input
            type="password"
            autoComplete="new-password"
            {...form.register('confirmPassword')}
          />
        </FormField>
        <Button type="submit" loading={loading} className="w-full" size="lg">
          {t('register.account.continue')}
        </Button>
      </form>
    </>
  );
}

function StepVerify({
  t,
  email,
  otp,
  onOtpChange,
  onVerify,
  verifying,
  onResend,
  resendCooldown,
  resending,
  onBack,
  inputRef,
}: {
  t: ReturnType<typeof useT>;
  email: string;
  otp: string;
  onOtpChange: (v: string) => void;
  onVerify: () => void;
  verifying: boolean;
  onResend: () => void;
  resendCooldown: number;
  resending: boolean;
  onBack: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <div className="mb-6 grid h-16 w-16 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)]">
        <UserIcon className="h-7 w-7" />
      </div>

      <h1 className="text-2xl font-bold">{t('register.verify.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
        {t('register.verify.subtitlePrefix')}{' '}
        <span className="font-semibold text-[var(--color-accent)]">{email}</span>
        {'.'}
      </p>

      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={otp}
        onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="······"
        className="mt-8 h-16 w-full rounded-xl border border-[var(--color-border-default)] bg-black/40 text-center font-mono text-3xl font-bold tracking-[0.6em] text-[var(--color-accent)] placeholder:text-[var(--color-fg-disabled)] focus:border-[var(--color-accent)] focus:outline-none"
      />

      <Button
        type="button"
        className="mt-6 w-full"
        size="lg"
        loading={verifying}
        disabled={otp.length !== 6}
        onClick={onVerify}
      >
        {t('register.verify.verify')}
      </Button>

      <div className="mt-6 text-center text-sm text-[var(--color-fg-muted)]">
        {t('register.verify.didntReceive')}{' '}
        <button
          type="button"
          disabled={resendCooldown > 0 || resending}
          onClick={onResend}
          className="font-semibold text-[var(--color-accent)] disabled:text-[var(--color-fg-subtle)]"
        >
          {resendCooldown > 0
            ? t('register.verify.resendIn', { seconds: resendCooldown })
            : t('register.verify.resendNow')}
        </button>
      </div>
    </>
  );
}
